// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/PermissionAndCompApproval.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { 
  X, Eye, Check, Ban,
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import ModernTable from "../ui/ModernTable";

export default function PermissionAndCompApproval({ user }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("permissions"); // permissions or compoffs

  // Lists Data
  const [permissions, setPermissions] = useState([]);
  const [compOffs, setCompOffs] = useState([]);

  // Detail Drawer Tracking States
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [approverNote, setApproverNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ── 1. DATA LOADING ─────────────────────────────────────────────────────────
  const fetchPendingRequests = useCallback(async () => {
    setLoading(true);
    try {
      const permUrl = SummaryApi.permissionReporteesList?.url || "/api/hr/permission/reportees?status=pending";
      const compUrl = SummaryApi.compOffReporteesList?.url || "/api/hr/compoff/reportees?status=pending";

      const [permRes, compRes] = await Promise.all([
        api.get(permUrl),
        api.get(compUrl)
      ]);

      if (permRes.data.success) {
        setPermissions(permRes.data.data?.requests || permRes.data.data || []);
      }
      if (compRes.data.success) {
        setCompOffs(compRes.data.data?.requests || compRes.data.data || []);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load approval requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  // Tab மாறும்போது பக்கத்தை 1 ஆக மாற்றுதல்
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // ── 2. APPROVAL / REJECTION OVERRIDE ACTION ─────────────────────────────────
  const handleProcessRequest = async (status) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      let url = "";
      if (activeTab === "permissions") {
        url = status === "approved"
          ?  SummaryApi.approvePermission.url(selectedRequest._id)
          :  SummaryApi.rejectPermission.url(selectedRequest._id);
      } else {
        url = status === "approved"
          ? SummaryApi.approveCompOff.url(selectedRequest._id) || `/api/hr/compoff/approve/${selectedRequest._id}`
          : SummaryApi.rejectCompOff.url(selectedRequest._id);
      }

      const res = await api.put(url, { approverNote });

      if (res.data.success) {
        toast.success(`Request ${status} successfully!`);
        await fetchPendingRequests();
        closeViewDrawer();
      } else {
        toast.error(res.data.message || "Failed to process request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error processing request.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Drawer Handlers ---
  const openViewDrawer = (request) => {
    setSelectedRequest(request);
    setApproverNote(request.approverNote || "");
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedRequest(null);
    setApproverNote("");
  };

  // ── 3. COLUMNS DEFINITIONS ─────────────────────────────────────────────────
  
  // Permissions Table Columns
  const permissionColumns = useMemo(() => [
    {
      key: "employee",
      label: "Employee",
      render: (row) => {
        const emp = row.userId || {};
        return (
          <div className="flex items-center gap-3">
            {emp.profilePicture ? (
              <img src={emp.profilePicture} alt="Avatar" className="h-8 w-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-[10px] font-bold shrink-0">
                {emp.firstName?.[0] || ""}{emp.lastName?.[0] || ""}
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-bold text-gray-800">{`${emp.firstName || ""} ${emp.lastName || ""}`}</span>
              <span className="text-[10px] text-gray-400 font-bold">{emp.userId || "No ID"}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: "date",
      label: "Requested Date",
      render: (row) => <span className="font-semibold text-slate-700">{new Date(row.date).toLocaleDateString()}</span>
    },
    {
      key: "timeInterval",
      label: "Time Interval",
      render: (row) => <span className="text-slate-600 font-bold">{row.startTime} - {row.endTime}</span>
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => <span className="text-slate-500 font-semibold">{row.durationMinutes} Minutes</span>
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => <span className="text-slate-400 italic max-w-xs truncate block">"{row.reason}"</span>
    }
  ], []);

  // Comp-off Table Columns
  const compOffColumns = useMemo(() => [
    {
      key: "employee",
      label: "Employee",
      render: (row) => {
        const emp = row.userId || {};
        return (
          <div className="flex items-center gap-3">
            {emp.profilePicture ? (
              <img src={emp.profilePicture} alt="Avatar" className="h-8 w-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-[10px] font-bold shrink-0">
                {emp.firstName?.[0] || ""}{emp.lastName?.[0] || ""}
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-bold text-gray-800">{`${emp.firstName || ""} ${emp.lastName || ""}`}</span>
              <span className="text-[10px] text-gray-400 font-bold">{emp.userId || "No ID"}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: "workedDate",
      label: "Worked Sunday / Holiday",
      render: (row) => <span className="font-semibold text-slate-700">{new Date(row.workedDate).toLocaleDateString()}</span>
    },
    {
      key: "reason",
      label: "Work Description",
      render: (row) => <span className="text-slate-500 italic max-w-sm truncate block font-medium">"{row.reason}"</span>
    }
  ], []);

  const tableActions = [
    {
      label: "View & Process Request",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    }
  ];

  // Pagination slice data
  const paginatedData = useMemo(() => {
    const list = activeTab === "permissions" ? permissions : compOffs;
    const startIdx = (currentPage - 1) * itemsPerPage;
    return list.slice(startIdx, startIdx + itemsPerPage);
  }, [activeTab, permissions, compOffs, currentPage]);

  const totalItemsCount = activeTab === "permissions" ? permissions.length : compOffs.length;

  if (loading) return <PageLoader />;

  return (
    <div className="p-4">
      
      {/* TABS CONTAINER */}
      <div className="overflow-hidden flex flex-col">
        {/* Sub-tabs header */}
        <div className="flex border-b bg-slate-50/50">
          <button
            onClick={() => setActiveTab("permissions")}
            className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "permissions" 
                ? "border-red-600 text-red-600 bg-red-50/10" 
                : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            Pending Permissions ({permissions.length})
          </button>
          <button
            onClick={() => setActiveTab("compoffs")}
            className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "compoffs" 
                ? "border-red-600 text-red-600 bg-red-50/10" 
                : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            Pending Comp-off Grants ({compOffs.length})
          </button>
        </div>

        {/* List Table container */}
        <div className="p-4">
          <ModernTable
            columns={activeTab === "permissions" ? permissionColumns : compOffColumns}
            data={paginatedData}
            actions={tableActions}
            actionType="dropdown"
            selectable={false}
            stickyColumns={1}
            pagination={{
              totalItems: totalItemsCount,
              itemsPerPage: itemsPerPage,
              currentPage: currentPage,
              onPageChange: setCurrentPage
            }}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE DETAILED APPROVAL / REJECTION DRAWER */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Review {activeTab === "permissions" ? "Permission" : "Comp-off"} Request
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Manager portal to approve or reject requests.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 text-xs">
              
              {/* Employee Summary Card */}
              <div className="bg-slate-50 p-4 rounded-xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center font-bold text-sm uppercase border border-gray-200">
                    {selectedRequest?.userId?.profilePicture ? (
                      <img src={selectedRequest.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
                    ) : (
                      `${selectedRequest?.userId?.firstName?.charAt(0) || ""}${selectedRequest?.userId?.lastName?.charAt(0) || ""}`
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800">
                      {selectedRequest?.userId ? `${selectedRequest.userId.firstName} ${selectedRequest.userId.lastName}` : ""}
                    </div>
                    <div className="text-[10px] text-gray-400 font-semibold">{selectedRequest?.userId?.email}</div>
                  </div>
                </div>
              </div>

              {/* Dynamic Parameters based on Active Tab */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Request Parameters</h4>
                <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm space-y-3">
                  {activeTab === "permissions" ? (
                    <>
                      <div className="flex justify-between items-center pb-2.5 border-b border-slate-50">
                        <span className="text-gray-500 font-medium">Requested Date:</span>
                        <span className="font-bold text-gray-800">{new Date(selectedRequest?.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between items-center pb-2.5 border-b border-slate-50">
                        <span className="text-gray-500 font-medium">Time Interval:</span>
                        <span className="font-bold text-gray-800">{selectedRequest?.startTime} - {selectedRequest?.endTime}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-medium">Total Duration:</span>
                        <span className="font-bold text-gray-800">{selectedRequest?.durationMinutes} Minutes</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-medium">Worked Sunday/Holiday:</span>
                      <span className="font-bold text-gray-800">{new Date(selectedRequest?.workedDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Submission Reason */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Employee Reason</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 text-sm text-gray-700 leading-relaxed font-medium">
                  "{selectedRequest?.reason || "No reasoning specified"}"
                </div>
              </div>

              {/* Approver Remarks Notes */}
              <div className="space-y-2 border-t border-gray-100 pt-5">
                <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Approver Remarks / Note</label>
                <textarea
                  rows={4}
                  value={approverNote}
                  onChange={(e) => setApproverNote(e.target.value)}
                  placeholder="Enter approval or rejection notes..."
                  className="w-full p-3 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-blue-100"
                />
              </div>
            </div>

            {/* Drawer Footer Action Buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => handleProcessRequest("rejected")}
                disabled={submitting}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors"
              >
                <Ban className="w-4 h-4" />
                <span>Reject</span>
              </button>

              <button
                type="button"
                onClick={() => handleProcessRequest("approved")}
                disabled={submitting}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
              >
                <Check className="w-4 h-4" />
                <span>Approve</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}